import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import promoBackground from "../../assets/promo_background.webp";
import { X } from "lucide-react";
import closeButton from "../../assets/close_promo_modal.svg";
import ctaButton from "../../assets/promo_cta.svg";
import { trackEvent } from "../../services/appsflyer";

interface PromoModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** When false, the parent controls when the modal opens via isOpen. Default true. */
  autoOpen?: boolean;
}

const PURCHASE_PATH = "/purshase";

export function PromoModal({ isOpen, setIsOpen, autoOpen = true }: PromoModalProps) {
  const navigate = useNavigate();
  const [isClosing, setIsClosing] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const openModal = useCallback(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    setIsOpen(true);
    setIsClosing(false);
    // Focus the close button on open for accessibility
    setTimeout(() => closeButtonRef.current?.focus(), 100);
  }, [setIsOpen]);

  const closeModal = useCallback(() => {
    setIsClosing(true);
    // Wait for the exit animation before actually closing the dialog, then route to purchase
    setTimeout(() => {
      dialogRef.current?.close();
      setIsOpen(false);
      setIsClosing(false);
      navigate(PURCHASE_PATH, { replace: true });
      trackEvent("paywall_viewed",{ source: "post_game" })
    }, 300);
  }, [setIsOpen, navigate]);

  // Auto-open after a short delay (only when parent does not control opening)
  useEffect(() => {
    if (!autoOpen) return;
    const timer = setTimeout(openModal, 600);
    return () => clearTimeout(timer);
  }, [autoOpen, openModal]);

  // Sync isOpen prop to dialog when parent controls opening (autoOpen === false)
  useEffect(() => {
    if (autoOpen) return;
    if (isOpen) {
      openModal();
    } else {
      const dialog = dialogRef.current;
      if (dialog?.open) {
        setIsClosing(true);
        const t = setTimeout(() => {
          dialogRef.current?.close();
          setIsClosing(false);
        }, 300);
        return () => clearTimeout(t);
      }
    }
  }, [autoOpen, isOpen, openModal]);

  // Handle backdrop click (click on the dialog element itself, not its children)
  const handleDialogClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (e.target === dialogRef.current) {
        closeModal();
      }
    },
    [closeModal],
  );

  // Handle native cancel event (Escape key)
  const handleCancel = useCallback(
    (e: React.SyntheticEvent<HTMLDialogElement>) => {
      e.preventDefault();
      closeModal();
    },
    [closeModal],
  );

  // Trap focus inside the dialog when open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;

      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const animationClass = isClosing
    ? "animate-modal-out"
    : isOpen
      ? "animate-modal-in"
      : "";

  const overlayAnimationClass = isClosing
    ? "animate-overlay-out"
    : isOpen
      ? "animate-overlay-in"
      : "";

  return (
    <dialog
      ref={dialogRef}
      onClick={handleDialogClick}
      onCancel={handleCancel}
      aria-label="Premium content promotion"
      className={`
        fixed inset-0 z-50 m-auto
        max-h-[90vh] w-[90vw] max-w-[420px]
        lg:max-w-[520px] lg:w-[min(90vw,520px)]
        overflow-visible bg-transparent p-0
        ${animationClass}
        backdrop:bg-black/70 backdrop:backdrop-blur-sm
        backdrop:${overlayAnimationClass}
      `}
      style={{
        /* Ensure the dialog content is transparent so our styled children show */
        background: "transparent",
        border: "none",
        outline: "none",
      }}
    >
      <div className="relative" role="document">
        {/* Close button */}
        <button
          ref={closeButtonRef}
          onClick={closeModal}
          aria-label="Close promotion"
          className="
            absolute -top-3 -right-3 z-10
            flex items-center justify-center
            size-9 rounded-full
            bg-[#1a1a1a] text-white
            border-2 border-[#F97316]
            shadow-lg shadow-black/30
            transition-all duration-200
            hover:scale-110 hover:bg-[#F97316]
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:ring-offset-2 focus-visible:ring-offset-black
            cursor-pointer
          "
        >
          <X className="size-4" aria-hidden="true" />
          <span className="sr-only">Close</span>
        </button>

        {/* Image container with glow effect */}
        <div className="relative group">
          {/* Outer glow */}
          <div
            className={`
              absolute -inset-1 rounded-2xl blur-md transition-opacity duration-700
              bg-gradient-to-b from-[#F97316]/40 to-[#F97316]/10
              ${imageLoaded ? "opacity-100" : "opacity-0"}
            `}
            aria-hidden="true"
          />

          {/* Card with image */}
          <div className="relative overflow-hidden rounded-xl ring-1 ring-white/10 shadow-2xl shadow-black/50">
            <img
              src={promoBackground}
              alt="Premium content promotion - upgrade for 24 hours of exclusive content"
              className={`
                block w-full h-auto
                transition-all duration-700 ease-out
                ${imageLoaded ? "opacity-100 scale-100" : "opacity-0 scale-105"}
              `}
              onLoad={() => setImageLoaded(true)}
            />

            {/* Subtle shimmer overlay on hover */}
            <div
              className="
                absolute inset-0 pointer-events-none
                bg-gradient-to-tr from-transparent via-white/5 to-transparent
                opacity-0 group-hover:opacity-100 transition-opacity duration-500
              "
              aria-hidden="true"
            />
            {/* CTA button - fills the space between the 4 arrows */}
            <button
              onClick={closeModal}
              aria-label="Get premium - 115 Riyals only"
              className="
              absolute left-[20%] right-[20%] cursor-pointer
              flex items-center justify-center
              transition-transform duration-200 ease-out
              hover:scale-[1.04] active:scale-[0.97]
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
              rounded-lg
            "
              style={{ top: "65%", height: "18%" }}
            >
              <img
                src={ctaButton}
                alt=""
                className="object-contain w-full h-full"
                aria-hidden="true"
              />
              <span className="sr-only">{"Get premium - 115 Riyals only"}</span>
            </button>

            {/* Close button - positioned at bottom-right near OMG area */}
            <button
              onClick={closeModal}
              aria-label="Close promotion"
              className="
              absolute cursor-pointer
              transition-transform duration-200 ease-out
              hover:scale-[1.06] active:scale-[0.95]
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
              rounded-md
            "
              style={{
                bottom: "8%",
                right: "2%",
                width: "42%",
                height: "6%",
              }}
            >
              <img
                src={closeButton}
                alt=""
                className="object-contain"
                aria-hidden="true"
              />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
}
